import { GEOSPACE_CHIBAN_TILE_LAYER_NAME } from "@/features/map/components/CommonChibanTileLayer";
import { NO_INFORMATION_LAYER_NAME } from "@/features/map/components/LeafletMap";
import { REAL_ESTATE_SEARCH_LAYER_NAME } from "@/features/map/components/layers/RealEstateSearchLayer";
import { SAVED_PLOT_LAYER_NAME } from "@/features/map/components/layers/SavedPlotLayer";
import {
  addMunicipalDocumentsLayer,
  addRequestedPicturesLayer,
  applyChibanchizuLayers,
  BLUE_MAP_LAYER_NAME,
  ZENRIN_RESIDENTIAL_LAYER_NAME,
} from "@/features/map/utils/urlParams/applyLayerParams";
import { parseMapUrlParams } from "@/features/map/utils/urlParams/parseMapUrlParams";

/** 契約に依らず常に表示する基本レイヤー群。 */
export const BASE_INITIAL_LAYERS = [
  NO_INFORMATION_LAYER_NAME,
  SAVED_PLOT_LAYER_NAME,
  REAL_ESTATE_SEARCH_LAYER_NAME,
];

/** ゼンリン契約者向けの初期レイヤー群（住宅地図・ブルーマップを含む）。 */
export const ZENRIN_INITIAL_LAYERS = [
  ...BASE_INITIAL_LAYERS,
  ZENRIN_RESIDENTIAL_LAYER_NAME,
  BLUE_MAP_LAYER_NAME,
];

/** GEOSPACE 契約者向けの初期レイヤー群（GEOSPACE 地番タイルを含む）。 */
export const GEOSPACE_INITIAL_LAYERS = [
  ...BASE_INITIAL_LAYERS,
  GEOSPACE_CHIBAN_TILE_LAYER_NAME,
];

/** 契約状態に応じた初期レイヤー群を選ぶ（ゼンリン優先）。 */
const selectInitialContractLayers = (
  hasZenrinContract: boolean,
  hasGeospaceContract: boolean
): string[] => {
  if (hasZenrinContract) return [...ZENRIN_INITIAL_LAYERS];
  if (hasGeospaceContract) return [...GEOSPACE_INITIAL_LAYERS];
  return [...BASE_INITIAL_LAYERS];
};

/**
 * `window.location.search` の URL クエリパラメータと契約状態から、初期 `checkedLayers` を計算する。
 *
 * - 契約に応じた基本レイヤー（`ZENRIN_INITIAL_LAYERS` / `GEOSPACE_INITIAL_LAYERS` / `BASE_INITIAL_LAYERS`）
 * - `?chibanchizu=true`: GEOSPACE 契約優先（ゼンリン系を除外し GEOSPACE 地番タイルを追加）。
 *   ゼンリンのみは既にブルーマップ ON のため実質変化なし。両契約なしはサイレント。
 * - `?requestedPicturesList=true`: `REQUESTED_PICTURES_PLOT_LAYER_NAME` を追加。
 * - `?municipalDocuments=true`: `MUNICIPAL_DOCUMENT_LAYER_NAME`（役所資料の黒塗りピン）を追加。
 * - `extraLayers`: 現在の `checkedLayers` から契約系初期レイヤーを除いた追加分（重複排除のうえ末尾に保持）。
 *
 * 動的な `setCheckedLayers` で追加するアプローチは LeafletMap 内部の `overlayVisibility` が
 * `useState` の遅延初期化で固定されるため UI に反映されない。
 * よって URL パラメータの解釈は本ヘルパで同期に行い、初期値（または契約取得直後の拡張）に組み込む。
 */
export const computeUrlBasedCheckedLayers = (
  hasZenrinContract: boolean,
  hasGeospaceContract: boolean,
  extraLayers: string[]
): string[] => {
  let layers = selectInitialContractLayers(
    hasZenrinContract,
    hasGeospaceContract
  );
  const urlParams = parseMapUrlParams(window.location.search);

  if (urlParams.chibanchizu) {
    layers = applyChibanchizuLayers(
      layers,
      hasZenrinContract,
      hasGeospaceContract
    );
  }

  if (urlParams.requestedPicturesList) {
    layers = addRequestedPicturesLayer(layers);
  }

  if (urlParams.municipalDocuments) {
    layers = addMunicipalDocumentsLayer(layers);
  }

  const newExtras = extraLayers.filter((extra) => !layers.includes(extra));
  return newExtras.length > 0 ? [...layers, ...newExtras] : layers;
};
